import {Component, EventEmitter, forwardRef, Input, OnChanges, Output, Provider, SimpleChanges} from '@angular/core';
import {ControlValueAccessor, FormGroup, NG_VALUE_ACCESSOR} from '@angular/forms';
import {PrestationType} from '@prestationType/model';
import {
  ALL_PRESTATION_TYPE_FILTER,
  PrestationTypeFilterInterface,
  VALUE_PRESTATION_TYPE_FILTER
} from '@shared/module/forms/component/form-multi-select/enum';
import {MultiSelectOption} from '@shared/module/forms/component';

const SELECT_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => FormMultiSelectComponent),
  multi: true,
};

@Component({
  selector: 'app-form-multi-select',
  templateUrl: './form-multi-select.component.html',
  styleUrls: ['./form-multi-select.component.scss'],
  providers: [SELECT_CONTROL_VALUE_ACCESSOR]
})
export class FormMultiSelectComponent implements ControlValueAccessor, OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() formControlName!: string;
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() options!: MultiSelectOption[];
  @Input() translateKey: string = '';
  @Input() type: string = 'default';
  @Input() hasFilter: boolean = false;
  @Input() canMultiCheck: boolean = false;
  @Input() maxHeight?: string;
  @Output() onChooseFilter = new EventEmitter<any>();
  @Output() onTriggerChange = new EventEmitter<any>();

  show = false;
  selected: PrestationType[] = [];
  disabled = false;
  filters: string[] = ALL_PRESTATION_TYPE_FILTER;
  selectedFilters = VALUE_PRESTATION_TYPE_FILTER;
  hasParent: boolean = false;
  selectAll : boolean = false;
  unselectAll: boolean = false;

  private onTouched!: Function;
  private onChanged!: Function;

  constructor() {
  }

  ngOnChanges(changes: SimpleChanges) {
    const selected = this.options?.filter(o => o.selected).map(o => o.value);
    if (selected) {
      this.writeValue(selected);
    }
  }

  selectValue(option: MultiSelectOption) {
    if (option.value.length > 0) {
      for (let o of option.value) {
        if (this.selected.filter((item: any) => item.id === o.id).length > 0) {
          this.selected = this.selected.filter((item: any) => item.id !== o.id);
        } else {
          this.selected.push(o);
        }
      }
    } else {
      if (this.selected.filter((item: any) => item.id === option.value.id).length > 0) {
        this.selected = this.selected.filter((item: any) => item.id !== option.value.id);
      } else {
        this.selected.push(option.value);
      }
    }
    this.onChanged(this.selected); // <-- call function to let know of a change
  }

  filterValue(a: any): void {
    const str: keyof PrestationTypeFilterInterface = a;
    this.selectedFilters[str] = !this.selectedFilters[str];
    this.onChooseFilter.emit(str);
  }

  writeValue(option: PrestationType[]): void {
    this.options?.filter(o => {
      if (o.parent) {
        this.hasParent = true;
      }
    });
    this.selected = option;
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn; // <-- save the function
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn; // <-- save the function
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  updateAllComplete(parent: MultiSelectOption) {
    parent.allSelected = parent.child != null && parent.child.every(c => c.selected);
    this.setFormGroupValue(parent.parent);
  }

  someComplete(parent: MultiSelectOption): boolean {
    if (parent.child == null) {
      return false;
    }
    return parent.child.filter(c => c.selected).length > 0 && !parent.allSelected;
  }

  setAll(selected: boolean, parent: MultiSelectOption) {
    parent.allSelected = selected;
    if (parent.child == null) {
      return;
    }
    parent.child.forEach(c => (c.selected = selected));
    this.setFormGroupValue(parent.parent);
  }

  setFormGroupValue(parent: any) {
    this.formGroup.value[this.formControlName] = [];
    this.options.forEach(parent => {
      if (parent.child) {
        parent.child.forEach(child => {
          if (child.selected) {
            const controlValue = this.formGroup.value[this.formControlName];
            this.formGroup.patchValue({
              [this.formControlName]: controlValue.concat(child.value)
            });
          }
        })
      } else {
        if (parent.selected) {
          const controlValue = this.formGroup.value[this.formControlName];
          this.formGroup.patchValue({
            [this.formControlName]: controlValue.concat(parent.value)
          });
        }
      }
    })
    this.triggerChange(parent);
  }

  triggerChange(parent: any) {
    const value = this.formGroup as any;
    value.team = parent;
    this.onTriggerChange.emit(value);
  }

  checkAll(event: any): void {
    if(!event.target.checked) {
      return;
    }

    for (let option of this.options) {
      option.selected = true;
    }

    this.formGroup.patchValue({
      [this.formControlName]: this.options.map(x => x.value)
    });

    this.unselectAll = false;
  }

  uncheckAll(event: any): void {
    if(!event.target.checked) {
      return;
    }

    for (let option of this.options) {
      option.selected = false;
    }

    this.formGroup.patchValue({[this.formControlName]: []});
    this.selectAll = false;
  }
}
